/**
 * The shapes on the wire, split the way the two entry points are split.
 *
 * These are the server's own bodies, field for field: `snake_case`, a slug
 * where another design would carry an opaque id, and a field absent rather than
 * `null` wherever the server leaves it out. Renaming them on the way in would
 * put a translation table between this console and its contract, and the first
 * thing such a table does is drift.
 *
 * Read the file top to bottom and the boundary is visible: account types never
 * mention a cluster's resource, cluster types never carry a tenancy that was
 * typed rather than resolved. The two meet in one place — the identity provider
 * a tenancy chose in the account decides who its cluster console lets in.
 *
 * The contract is `gdsgate-saas`, docs/api.md; docs/api-expectations.md in this
 * repository lists what the console still expects and does not get.
 */

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/**
 * A tenancy as the server names it, wherever it is named.
 *
 * Filled in by the server on both trees and never written from an address bar:
 * in the account it is the tenancy the caller represents, on a cluster console
 * it is the one whose domain answered.
 */
export interface TenancyRef {
  /** One DNS label. Also the cluster profile and the name of its directory. */
  slug: string;
  display_name: string;
  /** The tenancy's own domain. */
  tenancy_domain: string;
  /** Where the cluster console answers — the tenancy domain, spelled out. */
  console_domain: string;
  /** `proxy.<tenancy_domain>` — where clients and connectors dial. */
  gateway_domain: string;
}

// ---------------------------------------------------------------------------
// Identity provider — chosen in the account, enforced by the cluster
// ---------------------------------------------------------------------------

/**
 * Who issues the tokens a cluster trusts.
 *
 * `provider` — the identity provider the provider runs. The tenancy has one
 * less thing to operate and hands over enrolment and revocation of its own
 * people. `own` — the tenancy's own OIDC provider: its people stay its own, and
 * one revocation there closes both the console and access to the hosts.
 */
export type IdentityProviderKind = 'provider' | 'own';

/**
 * A choice, in exactly the shape the server stores and accepts.
 *
 * Flat rather than a union with a nested settings object, because that is what
 * `PUT /api/account/tenancy/identity` takes: for `kind: "provider"` the server
 * requires the remaining fields to be empty, since the coordinates of the
 * provider's own identity provider are a deployment setting and a field for
 * them in a request body would be a way to substitute somebody else's issuer.
 *
 * There is no administrator group here and no client secret. The group belongs
 * to the tenancy, is written into the Cedar policy its cluster was seeded with,
 * and does not move when the provider does. A secret has nowhere to go:
 * gdsgate's `[oidc]` has no key for one.
 */
export interface IdentityChoice {
  kind: IdentityProviderKind;
  /** Exactly the `iss` the tokens carry. Required for `own`, empty for `provider`. */
  issuer: string;
  /** The client registered for this cluster. Required for `own`. */
  client_id: string;
  /** Expected `aud`. Empty means the client id. */
  audience: string;
  /** Narrowing. Empty means every asymmetric algorithm; `HS*` is never accepted. */
  signing_algs: SigningAlg[];
}

export type SigningAlg =
  | 'RS256'
  | 'RS384'
  | 'RS512'
  | 'PS256'
  | 'PS384'
  | 'PS512'
  | 'ES256'
  | 'ES384'
  | 'EdDSA';

/** Every algorithm the cluster will accept. Symmetric ones are never offered. */
export const SIGNING_ALGS: readonly SigningAlg[] = [
  'RS256',
  'RS384',
  'RS512',
  'PS256',
  'PS384',
  'PS512',
  'ES256',
  'ES384',
  'EdDSA',
];

/** The provider's own identity provider, with the fields the server requires empty. */
export function providerChoice(): IdentityChoice {
  return { kind: 'provider', issuer: '', client_id: '', audience: '', signing_algs: [] };
}

/**
 * Where a change of provider has got to.
 *
 * One state means "under way", not two. Asking the provider and restarting Auth
 * into it are different things, but while either is happening nothing can act
 * on the difference, and afterwards `rejected` and `failed` say it: refused
 * before anything was touched, against applied and did not land.
 */
export type IdentityState = 'pending' | 'applying' | 'applied' | 'rejected' | 'failed';

/** Why the last change did not take, as a code the console turns into words. */
export type IdentityFailure =
  | 'issuer_unreachable'
  /**
   * The provider answered and its certificate did not verify — expired, not
   * trusted, or not for that name.
   *
   * Its own code and not `issuer_unreachable`, because the two send a person to
   * different places: a provider whose certificate ran out is up and answering
   * everybody else, and "did not answer" sends whoever owns it to look at a
   * firewall.
   */
  | 'issuer_certificate'
  | 'issuer_mismatch'
  | 'algorithm_unsupported'
  | 'signing_keys_unusable'
  | 'no_login_flow'
  | 'client_unknown'
  | 'cluster_unreachable'
  | 'unknown';

/**
 * A tenancy's identity provider: what was asked for, what the cluster runs,
 * what there is to go back to, and where a change has got to.
 *
 * Every identity route answers with this one block — declaring, applying, going
 * back and dismissing — so the console reads one shape and polls one address.
 */
export interface TenancyIdentity {
  /** Which tenancy this is. Nothing in the request named it. */
  tenancy: TenancyRef;
  state: string;
  /** What polling watches, so the set of states can grow and the polling cannot. */
  in_flight: boolean;
  /** What the tenancy last asked for. */
  desired: IdentityChoice;
  /** What the cluster runs. Absent until it has been brought up. */
  applied?: IdentityChoice;
  /** What ran before that — where going back aims. */
  previous?: IdentityChoice;
  /** Whether there is anything to go back to. */
  revertable: boolean;
  /**
   * The address the tenancy's provider returns a browser to after a sign-in,
   * derived from the tenancy domain.
   *
   * It has to be registered in the client at that provider — byte for byte —
   * or sign-in fails at the provider's end and never reaches us. Nothing here
   * can register it or check that it was registered, which is exactly why the
   * console shows it where the rest of the provider's coordinates are typed.
   * It does not change when the provider does.
   */
  redirect_uri: string;
  /**
   * Whether this deployment runs an identity provider of its own, and so
   * whether `kind: "provider"` can be chosen at all. `false` means the console
   * shows the option unavailable and says why; the server refuses it at the
   * edge with `provider_identity_unavailable`.
   */
  provider_available: boolean;
  /** That provider's issuer, so a screen names it instead of carrying it in its own source. */
  provider_issuer?: string;
  /** One line about the last attempt, in the server's words. */
  detail: string;
  failure?: string;
  started_at?: string;
  /** Absent while a change is running. */
  finished_at?: string;
  changed_at: string;
  /** Who asked for the last change. */
  actor?: string;
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

/**
 * What an account may do at all, before any question of which tenancy.
 *
 * The console hides what would be refused; it enforces nothing. These are read
 * off the session rather than derived from the role, so that "hides, does not
 * forbid" cannot be counted differently in two places.
 *
 * There is no `manage_catalog` here, and its absence is the contract's: no
 * account role carries it any more. A catalogue is written in the cluster
 * console, by a person that tenancy's own provider vouched for, and the account
 * tree has no route that writes one — a capability with no door behind it is a
 * button this console would draw on the wrong screen.
 */
export type Capability =
  | 'view_tenant'
  | 'create_tenant'
  | 'reconcile_tenant'
  | 'issue_join_token'
  | 'choose_identity_provider'
  /**
   * Move tenancies to another gdsgate version, and decide a wave is sound
   * enough for the next one to start.
   *
   * The provider's own operator and nobody else. Which version a fleet runs is
   * what the provider answers for, and signing a wave off decides when everyone
   * *else* moves — a decision no single tenancy has the standing to make. The
   * board itself reads under `view_tenant`, so a customer's representative sees
   * their own line and cannot move anything on it.
   */
  | 'roll_out_version'
  | 'delete_tenant';

/**
 * `provider_admin` does everything except the one decision that is the
 * customer's; `provider_support` is the read-only support desk; `tenant_admin`
 * is a customer's own representative.
 */
export type AccountRole = 'provider_admin' | 'provider_support' | 'tenant_admin';

const ACCOUNT_ROLES: readonly string[] = ['provider_admin', 'provider_support', 'tenant_admin'];

/** A role this console knows how to draw. Anything else is said to be unknown. */
export function isAccountRole(value: string): value is AccountRole {
  return ACCOUNT_ROLES.includes(value);
}

export interface AccountActor {
  email: string;
  display_name: string;
  /** The name a change is recorded under in the control plane's own trail. */
  audit_name: string;
}

export interface AccountSession {
  actor: AccountActor;
  /** A string rather than the union: an unknown role has to be shown, not crash. */
  role: string;
  capabilities: Capability[];
  /**
   * Set by the server, and only when the account represents exactly one
   * tenancy. Nothing in this console may write it from a route parameter.
   */
  tenancy?: TenancyRef;
  expires_at: string;
}

export interface SignInInput {
  email: string;
  password: string;
}

/**
 * Changing one's own password, having shown the current one.
 *
 * There is no reset by mail and no route that sets somebody else's: this
 * product has no mail, and a reset with no second channel is a way to take an
 * account over. Every session of the account closes with the change, this one
 * included.
 */
export interface ChangePasswordInput {
  current: string;
  next: string;
}

/** The shortest password the server accepts. Checked here so a trip is saved. */
export const PASSWORD_MIN_LENGTH = 12;

/** A report, never an instruction: only a reconcile moves a tenancy between these. */
export type TenantState =
  | 'requested'
  | 'provisioning'
  | 'ready'
  | 'failed'
  | 'suspended'
  | 'tearing_down'
  | 'removed';

export const TENANT_STATES: readonly TenantState[] = [
  'requested',
  'provisioning',
  'ready',
  'failed',
  'suspended',
  'tearing_down',
  'removed',
];

const TENANT_STATE_SET: readonly string[] = TENANT_STATES;

export function isTenantState(value: string): value is TenantState {
  return TENANT_STATE_SET.includes(value);
}

export interface Tenant {
  /** The key every other route takes. There is no opaque id. */
  slug: string;
  display_name: string;
  tenancy_domain: string;
  console_domain: string;
  gateway_domain: string;
  /**
   * The group the tenancy's Cedar policy names as administrators. Fixed when
   * the tenancy is declared, and unmoved by a change of identity provider.
   */
  admin_group: string;
  state: string;
  version: string;
  /** What a client pins on first contact. `null` until a gateway has run. */
  transport_ca_fingerprint: string | null;
  generation: number;
  /** Behind `generation` means there are changes the cluster has not been told about. */
  applied_generation: number | null;
  up_to_date: boolean;
  created_at: string;
  updated_at: string;
  /** On the card only, and read-only there: no route changes it by slug. */
  identity?: TenancyIdentity;
  /** On the card only. */
  last_reconcile?: ReconcileReport;
}

/**
 * What it takes to declare a tenancy.
 *
 * `identity` absent means the provider's own identity provider — the cluster is
 * stood up before its owner has been asked anything, and it has to be reachable
 * meanwhile. A deployment that runs no provider of its own refuses that with
 * `provider_identity_unavailable`, and then the operator names the customer's.
 */
export interface NewTenantInput {
  slug: string;
  display_name: string;
  /**
   * No domain here, and there cannot be one: a tenancy's two names follow from
   * the slug and the zone the deployment names tenancies in — `<slug>-app` and
   * `<slug>-proxy` — and the zone is the server's to know. A caller who could
   * name it could name a tenancy inside somebody else's.
   */
  version: string;
  /** Never empty: a cluster whose policy names no group is a cluster nobody administers. */
  admin_group: string;
  identity?: IdentityChoice;
}

export type StepOutcome = 'unchanged' | 'applied' | 'skipped' | 'failed';

export interface ReconcileStep {
  /** The step's number in the provisioning contract. */
  contract_step: number;
  step: string;
  outcome: string;
  detail: string;
}

/** `accepted` is the only outcome in which a tenancy is handed over. */
export type ReconcileOutcome = 'accepted' | 'not_accepted' | 'failed';

/**
 * How a pass over the tenancy's certificate came out.
 *
 * `kept` is the one worth reading twice, and it does not mean nothing happened:
 * it means the authority was **not asked**, because the pair on the disk is
 * good and covers every name the tenancy is published under. Let's Encrypt
 * counts its limits against the registered domain — the provider's, shared by
 * every tenancy — so reusing a valid certificate is what keeps a whole fleet
 * inside them, and asking again would be the fault.
 *
 * `foreign` is a pair the control plane did not install and will not touch;
 * `untended` is one it obtains none for at all, with the reason beside it.
 */
export type CertificateOutcome =
  | 'kept'
  | 'obtained'
  | 'waiting'
  | 'failed'
  | 'foreign'
  | 'untended';

/**
 * What the tenancy's certificate is, and what it is supposed to be.
 *
 * Both halves, because only the pair of them can say the thing nothing else
 * notices: that a name the tenancy is published under is not on the
 * certificate. That failure has no symptom on the provider's side — the
 * listener binds, the gateway serves — and shows up as clients refusing one of
 * the tenancy's names.
 */
export interface CertificateReport {
  /** A string rather than the union: an outcome neither side knows still draws. */
  outcome: string;
  /** The names the tenancy is published under, from the one place they are declared. */
  wanted: string[];
  /** The names the chain on the disk carries, read off the chain. Empty when there is none. */
  installed: string[];
  /** Published and not covered. Non-empty fails the step, and the tenancy is not handed over. */
  missing: string[];
  /** When what is installed stops being valid. `null` when nothing is installed. */
  not_after: string | null;
  /** When it will be replaced, if nothing changes. `null` when nothing is installed. */
  renew_at: string | null;
  /** Which authority this control plane last obtained one from. `null` when it obtained none. */
  authority: string | null;
  /**
   * Whether a client that was told nothing verifies it.
   *
   * Three answers rather than two. `false` is the honest state of a certificate
   * from the test authority: real TLS, and refused by anybody who was not told
   * about its root. `null` means the control plane did not install this pair and
   * makes no claim about the authority that did.
   */
  publicly_trusted: boolean | null;
  /** How many attempts in a row have failed. */
  attempts: number;
  /** The earliest the next attempt will be made. `null` when none is scheduled. */
  retry_after: string | null;
  /** One line an operator can act on. Carries no key and no token. */
  detail: string;
}

/**
 * Where a value the control plane rendered stands: whether the **process**
 * running the role has read it.
 *
 * A statement about the process and never about the file. A role reads its
 * configuration once, when it starts, so writing a value and the value taking
 * effect are two events with a window between them — and the file carries the
 * value either way, which is why it cannot answer. The server compares what was
 * running when the value was written against what is running now.
 *
 * Five answers, and what divides them is whether the answer is proved:
 *
 * - `in_force` — **proved read**: the process holding the role now is not the
 *   one that held it when the value was written, so it started afterwards;
 * - `not_read_yet` — **proved unread**: the same process, still holding what it
 *   read at its start. `detail` names what it is holding instead;
 * - `at_the_next_start` — nothing is running the role, so nothing is being held
 *   to anything;
 * - `not_known` — the question cannot be answered: the container runtime could
 *   not be asked, or there is no witness of when the value was written. Not a
 *   finding — an absence of knowledge, settled by the role's next start;
 * - `not_written` — the configuration step did not run on this pass. A statement
 *   about the pass, not about the process.
 */
export type SettingsState =
  | 'in_force'
  | 'not_read_yet'
  | 'at_the_next_start'
  | 'not_known'
  | 'not_written';

/** The budget's state is the same enumeration, worked out by the same witness. */
export type AnonymousBudgetState = SettingsState;

/**
 * What an anonymous caller of the tenancy's gateway is charged, per call.
 *
 * Two calls on the client-facing listener answer before there is anybody to
 * recognise — the settings bundle a first contact fetches and the health check
 * every sign-in opens with — and the budget is charged per call rather than per
 * connection, because one admitted HTTP/2 connection carries as many calls as
 * somebody sends.
 */
export interface AnonymousBudget {
  /** Calls a second from one source address, on each of those two. */
  rate_per_ip: number;
  /** How many such calls a momentarily idle address may make back to back. */
  burst: number;
  /** A string rather than the union: a state neither side knows still draws. */
  state: string;
  detail: string;
}

/**
 * Where every value rendered for one role stands.
 *
 * The general form of the budget above, and the reason the server has a field of
 * its own for it: the gap between a value being written and a role reading it
 * belongs to *every* value of the two configuration files the control plane
 * renders. The budget is only the one whose gap is a hole in a listener.
 */
export interface RoleReading {
  /** `auth` or `proxy`, as the rendered compose file names the service. */
  role: string;
  /**
   * The role's weakest answer over all its values: one value proved unread makes
   * the whole role `not_read_yet`. A string rather than the union, as above.
   */
  state: string;
  /**
   * **The values the running process cannot have read — names, never values.**
   *
   * Names in the spelling of gdsgate's own diagnostics
   * (`[proxy].public_anonymous_burst`), so that `doctor` and this call the same
   * thing the same thing. A name is enough to act on, and the same field would
   * carry a secret the day one of these values is one — so nothing here prints
   * anything but the name, whatever arrives.
   */
  unread: string[];
  /** One line an operator can act on. */
  detail: string;
}

/** Where the values of every role stand — always both roles. */
export interface RenderedSettings {
  /**
   * One entry per role, and the server sends all of them, so that a reader never
   * has to decide whether a missing role means "nothing pending" or "not looked
   * at".
   */
  roles: RoleReading[];
}

export interface ReconcileReport {
  tenant: string;
  generation: number;
  actor: string;
  started_at: string;
  finished_at: string;
  steps: ReconcileStep[];
  outcome: string;
  transport_ca_fingerprint: string | null;
  /**
   * What the tenancy's certificate is and what it should be.
   *
   * Optional here and not on the wire: a report is stored as it was written, so
   * one from before the certificate step carries no such block. A console that
   * required it would fail to draw the older report rather than say what is
   * missing from it.
   */
  certificate?: CertificateReport;
  /** The budget its anonymous surface is held to. Optional for the same reason. */
  anonymous_budget?: AnonymousBudget;
  /** Where every value of every role stands. Optional for the same reason. */
  rendered_settings?: RenderedSettings;
}

export interface ReconcileRun {
  actor: string;
  report: ReconcileReport;
}

/**
 * One line of what this control plane did to one tenancy.
 *
 * `action` and `outcome` are the server's own words — `tenant.reconcile`,
 * `catalog.put`, `rollout.move`; `success`, `accepted`, `failed` — and they are
 * shown as they arrive. The set grows on the server's side, so a console that
 * translated them would go quiet on the entry that mattered on the day a new
 * one appeared.
 */
export interface TrailEntry {
  /** Monotonic. The order the control plane wrote them in. */
  seq: number;
  at: string;
  /** The person behind it, never the machine. */
  actor: string;
  action: string;
  outcome: string;
  detail: string;
}

/**
 * A tenancy's trail: the last hundred lines, newest first.
 *
 * A hundred is the server's own limit and there is no cursor, so this is a
 * window on the end of the trail rather than the trail. The screen says so
 * where it counts them, and there is nothing here to page with.
 */
export interface Trail {
  entries: TrailEntry[];
}

// ---------------------------------------------------------------------------
// What a tenancy used
// ---------------------------------------------------------------------------

/**
 * The window a figure is counted over: half-open, `[from, to)`.
 *
 * Both ends are required and the server defaults neither. That is why this is
 * one type with two required fields rather than two optional strings — a period
 * with one end is not a period, and there is nowhere here to put half of one.
 */
export interface UsagePeriod {
  /** RFC 3339. The first instant counted. */
  from: string;
  /** RFC 3339. The first instant **not** counted. */
  to: string;
}

/** One connector of the tenancy's, as its registry row reads. */
export interface UsageAgent {
  /** The registry id. This is the checked one — a revocation takes it. */
  id: string;
  /**
   * What the node called itself when it joined.
   *
   * Asserted by the node and verified by nobody: a provision token is issued
   * for a role, not for a machine. Absent when the node named itself nothing.
   */
  claimed_name?: string;
  /** Absent when the registry printed a stamp that is not an instant. */
  enrolled_at?: string;
}

/**
 * The connectors, **as of the moment the registry was read** — not over a period.
 *
 * gdsgate's registry is current state with no history in it, so there is no
 * such thing as "connectors during July" to ask for.
 */
export interface AgentUsage {
  as_of: string;
  /** Live identities of role `connector`. Not connectors whose tunnel is up: no such signal exists. */
  active: number;
  /** Revoked identities. Not billed; here so that a fall in `active` has an answer. */
  revoked: number;
  connectors: UsageAgent[];
}

/** The catalogue **the cluster serves**, as of the moment it was read. */
export interface ResourceUsage {
  as_of: string;
  count: number;
  by_kind: Record<string, number>;
}

/** The sessions that **opened** inside the period. */
export interface SessionUsage {
  /** How many opened. A session belongs to the window it opened in, and to that one only. */
  count: number;
  closed: number;
  /** Counted in `count`, contributing nothing to `duration_secs`. */
  unclosed: number;
  /** Whole seconds, covering the sessions that closed and nothing else. */
  duration_secs: number;
  /**
   * Bytes of session **recording**, and never bytes relayed. gdsgate puts the
   * size of the recorded cast here, so a database, Kubernetes, MCP or plain TCP
   * session closes at zero however much traffic crossed it.
   */
  recorded_bytes: number;
  /** Closes whose opening was never seen. In no other figure — reported so a short total says so. */
  closed_without_open: number;
  by_kind: Record<string, number>;
}

/** The people who signed in inside the period. */
export interface PeopleUsage {
  /** **Distinct people.** This is the figure. */
  count: number;
  /**
   * How many sign-in rows those people accounted for.
   *
   * Never a figure of its own: gdsgate files at most one row per person per day
   * per Auth process, so restarts and a second instance add rows without adding
   * anybody. It is published beside `count` precisely so nobody reads it as one.
   */
  rows: number;
}

/** The four figures an answer can be missing. */
export type UsageFigureName = 'connectors' | 'resources' | 'sessions' | 'people';

/**
 * A figure that could not be counted, and why.
 *
 * `figure` is a plain string rather than the union: a name this console does
 * not know still has to be shown, because it is a gap in what somebody is
 * billed from.
 */
export interface UsageGap {
  figure: string;
  reason: string;
}

/**
 * What one tenancy used.
 *
 * A figure is either present or `null` **with a reason** in `unavailable`.
 * There is no third state, the server cannot construct one, and this console
 * must not draw one: zero is a number an invoice can be written from, and
 * "could not be read" is not.
 */
export interface TenantUsage {
  tenant: string;
  period: UsagePeriod;
  connectors: AgentUsage | null;
  resources: ResourceUsage | null;
  sessions: SessionUsage | null;
  people: PeopleUsage | null;
  /** Empty means all four are here. */
  unavailable: UsageGap[];
}

// ---------------------------------------------------------------------------
// Versions, and rolling one out
// ---------------------------------------------------------------------------

/**
 * The versions a rollout may be pointed at, as the registry holds them.
 *
 * `available: false` is not an error and must not be drawn as one: it means
 * this deployment has no registry to list — local images, or one that could not
 * be reached — and the version is typed instead, exactly as it always was.
 * `detail` says which of the two happened, in the server's words.
 *
 * A name being in `versions` says an image exists under it. It says nothing
 * about whether a tenancy may move there: that is the board's answer, and it
 * comes back from starting the rollout.
 */
export interface OfferedVersions {
  available: boolean;
  /** Releases first, newest first; moving tags and pre-releases after them. */
  versions: string[];
  /** Why there is no list. Empty when there is one. */
  detail: string;
}

/**
 * Which wave a tenancy moves in.
 *
 * `rest` until somebody says otherwise, and that default is the cautious one:
 * a tenancy nobody thought about must not be the one that finds out first.
 */
export type RolloutWave = 'canary' | 'rest';

export const ROLLOUT_WAVES: readonly RolloutWave[] = ['canary', 'rest'];

const ROLLOUT_WAVE_SET: readonly string[] = ROLLOUT_WAVES;

export function isRolloutWave(value: string): value is RolloutWave {
  return ROLLOUT_WAVE_SET.includes(value);
}

/**
 * What one component of a tenancy's deployment was seen running.
 *
 * The shape carries the difference this whole screen exists for: `version` is
 * what something **answered**, and it is absent — with `reason` in its place —
 * whenever nothing answered. The tenancy's declared image tag is a separate
 * field on a separate type and is never substituted here.
 *
 * `read_by` says how a version for this component is come by at all, and so how
 * much the answer is worth. Absent means there is no way to read this component
 * whatsoever, which is a different statement from "it was not read this time".
 */
export interface ComponentVersion {
  /** `client`, `proxy`, `auth` or `connector`. A string: an unknown one still draws. */
  component: string;
  /** What it is running. Absent when nothing answered. */
  version?: string;
  /** Why there is no version. Present exactly when `version` is absent. */
  reason?: string;
  /** How this component's version is come by. Absent when it cannot be read at all. */
  read_by?: string;
}

/**
 * Whether two components can talk to each other.
 *
 * Four verdicts and one of them is red. `no_named_break` is the ordinary answer
 * for a fleet with two versions in it: it says only that gdsgate has named
 * nothing breaking between those two, which is neither a fault nor a promise of
 * compatibility. `broken` is the one worth acting on.
 */
export type PairVerdict = 'same' | 'no_named_break' | 'broken' | 'untold';

export interface ComponentPair {
  /** The two, in the fixed order the server writes them. */
  between: [string, string];
  /** A string rather than the union: an unknown verdict is shown, not crashed on. */
  verdict: string;
  /** What is broken, or why nothing can be said. Absent when both run one build. */
  detail?: string;
  /** Where gdsgate said so. Empty unless the verdict is `broken`. */
  named_in?: string[];
}

/** One change gdsgate has itself named as breaking, as a forecast reads it. */
export interface NamedBreak {
  /** The release that carries it. */
  release: string;
  /** The pairs it stops working. */
  between: [string, string][];
  /** What happens, in gdsgate's own words. */
  what: string;
  /** Where gdsgate said so — a claim about somebody else's product, made checkable. */
  named_in: string;
}

/** What a rollout did to one tenancy. */
export interface TenantMove {
  /** `pending`, `moved` or `failed`, as of the last attempt. */
  outcome: string;
  /**
   * What it was on before this rollout touched it.
   *
   * Written once: by a second attempt the tenancy is already carrying the
   * target, so rewriting it would lose what it came from.
   */
  from: string;
  detail: string;
  /** How many times this rollout moved this tenancy. `1` in the ordinary case. */
  attempts: number;
  /**
   * How many of those attempts did not work.
   *
   * The board's memory of trouble, and the reason it is a field of its own:
   * `outcome: "moved"` with `failures: 1` is a tenancy that failed and was
   * moved after somebody put it right, which is a different fact from one that
   * moved first time. Without it the repair shows only in the trail.
   */
  failures: number;
  changed_at: string;
}

/** One tenancy's line on the board. */
export interface TenantVersions {
  slug: string;
  state: string;
  wave: string;
  /**
   * Whether a rollout may move it. A suspended or removed tenancy is in no
   * wave's arithmetic: moving it would mean reconciling it, which would put
   * back exactly what was taken away.
   */
  movable: boolean;
  /**
   * The image tag the tenancy is declared with — **what somebody typed**. It
   * says what the next start of a container will pull and nothing about what is
   * running now.
   */
  declared: string;
  /** Whether its last reconcile was accepted. */
  accepted: boolean;
  /** When its components were last read. Absent when they never were. */
  observed_at?: string;
  components: ComponentVersion[];
  pairs: ComponentPair[];
  /** What the rollout under way did to it, when there is one and it got that far. */
  step?: TenantMove;
}

/** One wave, and how much of the fleet this caller can see in it. */
export interface WaveSummary {
  wave: string;
  /** Its place in the order, counting from zero. */
  position: number;
  /** How many visible tenancies are in it and movable. */
  tenants: number;
  under_way: boolean;
}

/** Whether the wave under way may be signed off. */
export interface RolloutAdvance {
  may: boolean;
  /**
   * The refusal, in the server's own words, naming the tenancies to go and look
   * at. It is the sentence signing off would answer with, published before the
   * button is pressed — so the console shows it and writes none of its own.
   */
  blocked_by?: string;
  /** Which wave would start. Absent when signing off would finish the rollout. */
  next?: string;
}

/**
 * Whether the tenancies of the wave under way that did not move may be moved
 * again.
 *
 * The way out of a wave one tenancy failed in. A failed move holds its wave on
 * purpose, and once the tenancy has been put right this is what moves it — not
 * a way past the wave: only the ones that did not move are moved, the wave
 * still has to be signed off, and the next one still waits for that.
 */
export interface RolloutRetry {
  may: boolean;
  /** The refusal, in the server's own words. */
  blocked_by?: string;
  /**
   * Whose moves would be run again, in slug order. Absent when there are none —
   * the server leaves an empty list out.
   */
  tenants?: string[];
}

/** The rollout on record: the one going, or the last one that ended. */
export interface Rollout {
  target: string;
  /** `applying`, `waiting`, `halted` or `done`. */
  state: string;
  /**
   * Whether it is still going.
   *
   * The field every decision on this screen is made by, rather than a list of
   * state names held here: `applying` and `waiting` are going, `halted` and
   * `done` are over. A rollout that is over stays on the board — it is what
   * says who was moved and who was not — so "is one going" and "is one shown"
   * are two different questions.
   */
  live: boolean;
  /** The wave being moved, or waiting to be signed off. */
  wave: string;
  detail: string;
  /** Who started it. */
  actor: string;
  started_at: string;
  /** When the state last moved — what the soak is counted from. */
  changed_at: string;
  /** When it ended. Absent while it is going. */
  finished_at?: string;
  advance: RolloutAdvance;
  retry: RolloutRetry;
  /**
   * The named breaks this move crosses, worked out from what the fleet is
   * **seen** running rather than from what it is declared to run.
   *
   * The forecast, and the useful one: it is how "a rolling upgrade" and "a flag
   * day for every client in the field" are told apart before anything moves.
   */
  crosses: NamedBreak[];
  /** Why there is no forecast. A floating tag names a different build tomorrow. */
  forecast_unavailable?: string;
}

/**
 * The whole board: the rollout on record, the waves, and every tenancy this
 * caller may see.
 *
 * `rollout` is absent only where no rollout was ever started; otherwise it is
 * the one going or the last one that ended, and `live` is what tells them
 * apart. Every route that moves anything answers with this same shape, so the
 * console reads one thing.
 */
export interface RolloutBoard {
  rollout?: Rollout;
  waves: WaveSummary[];
  tenants: TenantVersions[];
}

// ---------------------------------------------------------------------------
// Cluster console
// ---------------------------------------------------------------------------

/**
 * What a resource is, in gdsgate's own spellings.
 *
 * `web-egress` carries a hyphen and not an underscore, because that is the one
 * spelling gdsgate takes — in TOML, on the wire and in `--kind`. Renaming it
 * here would produce a resource this console declares and a connector does not
 * recognise, and nothing on either side would say so.
 */
export type ResourceKind =
  | 'ssh'
  | 'postgres'
  | 'mysql'
  | 'kubernetes'
  | 'tcp'
  | 'mcp'
  | 'llm'
  | 'web'
  | 'web-egress';

/**
 * Every kind this console knows, in the order it lays them out.
 *
 * Not the same thing as the kinds a given tenancy may be offered: that is
 * `ClusterSession.resource_kinds`, and it is the server's answer because it
 * depends on the build the tenancy runs. This list is what the console has
 * words and pictures for.
 */
export const RESOURCE_KINDS: readonly ResourceKind[] = [
  'ssh',
  'postgres',
  'mysql',
  'kubernetes',
  'tcp',
  'mcp',
  'llm',
  'web',
  'web-egress',
];

const RESOURCE_KIND_SET: readonly string[] = RESOURCE_KINDS;

export function isResourceKind(value: string): value is ResourceKind {
  return RESOURCE_KIND_SET.includes(value);
}

/**
 * One row of the **declared** catalogue — what the control plane will make
 * true, not what the cluster holds at this second. How far behind the cluster
 * is shows on the tenancy's card in the account, as `up_to_date`.
 *
 * There is no serving connector here and no health: gdsgate has a field for
 * neither, and a column the server cannot fill is a column that invents its
 * contents.
 */
/** What the absence of a row inside a boundary means. */
export type ScopeFallback = 'parent' | 'deny';

/** Where a discovered `ssh` target's host key comes from. */
export type ScopeHostKey = 'annotation' | 'tofu';

/**
 * One boundary a connector fills by itself: inside it the connector writes
 * catalogue rows, each holding a lease it has to renew.
 *
 * The rows are not here. They belong to the cluster, they disappear when
 * nothing renews them, and the catalogue listing is where they are read.
 */
export interface Scope {
  /** The prefix every id inside it starts with. */
  prefix?: string;
  /** The connector allowed to publish here, by registry id. */
  node: string;
  /** Kinds a target may be published as. */
  kinds: ResourceKind[];
  /** Project written on every row here. */
  project?: string;
  /** Environment written on every row here. Never taken from the source. */
  environment: string;
  /** Labels written on every row here, over the ones the source claimed. */
  labels?: Record<string, string>;
  /** Label keys accepted from the source; anything else is dropped. */
  accept_label_keys?: string[];
  /**
   * How many rows it may hold at once, counting rows from earlier snapshots
   * whose leases have not run out.
   */
  max_live: number;
  /** How long a row stays after the snapshot that carried it. */
  lease_secs: number;
  fallback?: ScopeFallback;
  ssh_host_key?: ScopeHostKey;
  /** Whether it accepts publishes. Turning it off deletes the rows it holds. */
  enabled?: boolean;
  /**
   * How many rows it holds right now, read from the cluster. Absent where the
   * cluster could not be asked, which is not the same as none.
   */
  rows?: number;
}

export interface Resource {
  /** The routing key. Present on everything the server hands back. */
  id?: string;
  kind: string;
  project: string;
  environment: string;
  /** Become the resource's Cedar entity tags. Replaced whole on every write. */
  labels: Record<string, string>;
  /** For a database on a host. */
  parent?: string;
  /** Resource-local approval threshold. */
  min_approvers?: number;
  /** The name clients address it by. Absent means the id. */
  hostname?: string;
  aliases?: string[];
  /**
   * Absent means the well-known port of the kind, which `tcp`, `mcp`, `llm` and
   * `web-egress` lack.
   */
  port?: number;
  /**
   * Whether this MCP server's tool descriptors are pinned: a call on a tool
   * whose descriptor changed since it was first seen is refused until the
   * change is re-approved. `mcp` only; absent is off.
   */
  pin_tool_descriptors?: boolean;
  /**
   * The boundary that vouches for this row, absent where a person declared it.
   * A row inside a boundary was written by a connector and goes when nobody
   * renews it.
   */
  scope?: string;
  /** When its lease runs out (Unix seconds). Absent on a declared row. */
  lease_expires_at?: number;
  /**
   * The domain a `web` application answers on (`app.example.com`) — what its
   * connector puts in `Host` when it forwards, as that connector's backend
   * `url` spells it. `web` only; absent is the ordinary case.
   *
   * Not a second `hostname`. That one is a single label this cluster hands out
   * and a client resolves in its own zone; this is a whole name somebody else
   * owns, and declaring it publishes nothing.
   */
  native_name?: string;
}

/**
 * A resource as it is written: the whole of it, every time.
 *
 * `PUT /api/cluster/resources/{id}` replaces rather than merges — gdsgate
 * rewrites labels, addressing, parent and threshold on every write — so a form
 * that sent only what changed would silently drop the rest. The id is not here
 * because it is the path: the call says "make the resource called this look
 * like that", which is why it is a `PUT` and why running it twice is the same
 * as running it once.
 */
export interface ResourceInput {
  kind: ResourceKind;
  project: string;
  environment: string;
  labels: Record<string, string>;
  parent?: string;
  min_approvers?: number;
  hostname?: string;
  aliases?: string[];
  port?: number;
  /** `mcp` only. Absent is off, and off is what leaving it out means on the
   * server too: the row is written whole on every call. */
  pin_tool_descriptors?: boolean;
  /** `web` only. Sent for no other kind: the server refuses it there, and a
   * name stored on a kind that cannot answer on one would resolve nowhere. */
  native_name?: string;
}

/**
 * The person a tenancy's identity provider vouched for.
 *
 * No address: a provider is entitled not to release one, and a console that
 * demanded it would refuse people whose provider is merely strict.
 */
export interface ClusterActor {
  subject: string;
  display_name: string;
  /** What the tenancy's policy reads. */
  groups: string[];
}

/** `none` means signed in with nothing to administer. Not an error, not a refusal. */
export type ClusterRole = 'cluster_admin' | 'none';

export interface ClusterSession {
  actor: ClusterActor;
  role: string;
  tenancy: TenancyRef;
  /**
   * The kinds of resource this tenancy's own build understands, in the order to
   * offer them.
   *
   * The server's answer rather than this console's list, because it is per
   * tenancy: a fleet moves in waves, and a kind that arrived in a release after
   * this tenancy's build is a word its cluster has never heard. Offering it
   * would be laying out a refusal.
   *
   * Absent from a server that predates the field — then the console falls back
   * on everything it knows, which is what it did before the field existed.
   */
  resource_kinds?: string[];
}

/** `reconfiguring` and `not_configured` are not errors: the cluster runs, the way in does not. */
export type SignInStatus = 'ready' | 'reconfiguring' | 'not_configured';

/**
 * Whether this cluster console can let anybody in, and through whom.
 *
 * Everything here is already known to whoever typed the domain: that it is a
 * tenancy of ours, whose it is, and which provider its people come through. No
 * resource, no person and no state of the tenancy leaves through this route.
 */
export interface SignInOffer {
  status: string;
  /** Since when it has been in that state. */
  since: string;
  /** The way out of both states that are not `ready`, and it is on another domain. */
  account_url: string;
  tenancy: TenancyRef;
  /** The provider the cluster runs, when it runs one. */
  provider?: IdentityChoice;
  /**
   * Where the provider returns the browser — the same address the tenancy has
   * to register at its provider. It is here so that somebody who has just been
   * refused by their provider can read the expected address off the screen they
   * were refused on, instead of asking us for it.
   */
  redirect_uri: string;
}

/**
 * What starting a sign-in produces: somewhere to send the browser, and a
 * deadline.
 *
 * The console keeps none of it. The `state`, the `nonce` and the PKCE verifier
 * stay on the server under an opaque key it hands this browser as a
 * short-lived cookie; what comes back here is the address to leave for, the
 * address the provider will return to, and when the attempt stops being valid.
 */
export interface ClusterSignInStart {
  authorization_url: string;
  redirect_uri: string;
  expires_at: string;
}

/** What the provider sent back to `/login`, and all this console reads of it. */
export interface SignInCallback {
  code: string;
  state: string;
}

/**
 * One resource a connector says it serves.
 *
 * **Two halves, and this console must not blur them.** `node_id`, `offered_at`
 * and `declared` are the cluster's own; `resource_id`, `kind` and
 * `claimed_name` are what the connector called them — a join token is minted
 * for a role and not for a machine, so nobody verified any of the three.
 *
 * An offer is not access either: `declared: false` means the cluster routes
 * nothing to it, however healthy the connector serving it, until somebody
 * declares it in the catalogue.
 */
export interface OfferedResource {
  /** Registry id of the offering node — what joins this to a {@link ClusterNode}. */
  node_id: string;
  /** What that node called itself when it joined. Claimed, never checked. */
  claimed_name?: string;
  /** Resource id the connector claims to serve. */
  resource_id: string;
  /** `ssh`, `postgres`, …, or `unknown` when it reached the cluster without one. */
  kind: string;
  /** Unix seconds the declaration was last restated. */
  offered_at: number;
  /** Whether the tenancy's catalogue holds a row under this id. */
  declared: boolean;
}

/**
 * One link of a tenancy's audit chain — who did what, to what, and how it
 * ended.
 *
 * **Two halves, and this console must not blur them**, the same way
 * {@link OfferedResource} must not. Everything here but `claimed_operator` is
 * what the cluster itself checked and wrote; that one is a name a caller
 * asserted, and it has to be shown saying so.
 *
 * **Not a proof.** These are the rows the tenancy's Authority answered with. A
 * chain somebody had rewritten would answer just as smoothly — what settles
 * that is `verify-audit` against the cluster's database, and nothing a browser
 * can reach.
 */
export interface AuditEvent {
  /** Unix seconds. */
  at: number;
  /**
   * `AdminAction`, `SessionActivity`, `Authorization`, `ResourceIntegrity`, …
   * as gdsgate named it.
   *
   * **The class the row was sealed under**, which is not always the class the
   * same event would be given today. gdsgate split `Authorization` — the MCP
   * descriptor rows are `ResourceIntegrity` and the refusal for a resource
   * nothing routes to is `SessionActivity` — and the rows sealed before that
   * keep what they were sealed with, because the class is in the row's digest
   * and a chain whose classes were tidied up would no longer verify. A reader
   * that has to be right about both epochs narrows by {@link code}.
   */
  class: string;
  /**
   * What was done, in the cluster's own word.
   *
   * **Not from a fixed set, and not resolved against one here.** gdsgate has
   * renamed its events — `ssh.session.open` and five like it became
   * `session.open`, `authenticate` became `auth.signin`, `editPolicy` became
   * `policy.edit` — and a chain keeps every word it ever wrote, so both
   * vocabularies reach this console out of one tenancy.
   *
   * Every policy decision a current cluster writes carries the same word,
   * `policy.decision`, with the schema action it decided in
   * {@link cedar_action}. A screen showing decisions prints that one instead;
   * see `actionWord`.
   */
  action: string;
  /**
   * What happened, said as a number: `SES-0101`, `AUT-0101`, `POL-0201`.
   *
   * The one part of a row that survives a renaming: a code is never reused and
   * never renamed, so it means the same thing in a row sealed today and in one
   * sealed after the next reform.
   *
   * **Absent on rows a cluster too old to write one sealed**, and absence is
   * shown as absence. Any stand-in — a dash, an empty string, `unknown` — is a
   * value, and a value is something a reader and a filter would both have to be
   * told is not a code.
   */
  code?: string;
  /**
   * The Cedar schema action a decision was taken on: `connect`, `kubeRequest`,
   * `dbQuery`, …
   *
   * **Resolved by the control plane for both epochs**, which is why it is a
   * field rather than something read out of `detail` here: on a current cluster
   * it is a key of the detail object, on an older one it was the `action`
   * column itself, and this console never has to know which sort of row it is
   * holding.
   *
   * Absent on every row that is not a policy decision — including the refusal
   * for a resource nothing routes to, where no policy was asked at all.
   */
  cedar_action?: string;
  /** Who did it, as the cluster verified them. */
  actor: string;
  /** What it was done to. */
  target: string;
  /** `Allow`, `Deny`, `Success` or `Failure`. */
  outcome: string;
  /**
   * The particulars, as one sealed string — in either of two forms.
   *
   * A current cluster writes a **JSON object** whose keys are its own
   * dictionary (`session_uid`, `kind`, `reason`, `query`); an older one wrote
   * `key=value` pairs. Both are here forever: what a chain sealed cannot be
   * rewritten, so a reader tells the forms apart by the row itself — see
   * `asObject` — and never by which tenancy it came from.
   */
  detail: string;
  /**
   * Whether `detail` was cut to fit the page. Shown as a cut, never dropped: a
   * decision rendered without its context is a different claim from one that
   * carried none.
   */
  detail_truncated: boolean;
  /**
   * Who the caller *said* was behind an administrative call. Asserted, never
   * checked. Absent on every row of a cluster with no external control plane,
   * which is most rows of most clusters.
   */
  claimed_operator?: string;
}

/** What narrows a read of the chain. Every field optional. */
export interface AuditFilter {
  /**
   * One class, as gdsgate spells it for `--class` (`admin-action`, …) — which
   * is not how {@link AuditEvent.class} spells the same class. {@link classWord}
   * is the conversion.
   */
  class?: string;
  /** Exact verified actor. */
  actor?: string;
  /**
   * Action prefix (`enroll.`, `session.`).
   *
   * Nothing in this console sets it, and the reason is worth keeping: a prefix
   * matches against whichever vocabulary the rows were sealed in, and it
   * separates no decisions at all on a current cluster, where every decision
   * carries the one name. Narrowing decisions is what the code is for.
   */
  action?: string;
  /** Exact target. */
  target?: string;
  /** Inclusive floor on event time, Unix milliseconds. */
  since_ms?: number;
  /** Rows wanted. The API holds this to its own ceiling. */
  limit?: number;
  // No `code` here, though the route takes one. A tenancy still running a
  // gdsgate that has never heard of `--code` refuses the read outright rather
  // than answering with no rows, so the filter would work on some tenancies and
  // black out the whole screen on others — and which it does is not something
  // this console can see before it asks. The code is shown; narrowing by it
  // waits for the control plane to decide what an old tenancy answers.
}

/**
 * The word `--class` takes for a class as a row carries it: `SessionActivity`
 * → `session-activity`.
 *
 * The two spellings are one contract with two ends — a chain writes the class
 * as gdsgate names it in code, and the command line behind
 * {@link AuditFilter.class} takes that same name in kebab case — so anything
 * matching a row against a filter has to cross between them.
 *
 * **A rule, not a table.** The classes are the variants of one enum, and this
 * is the conversion the command line itself applies, so a class this console
 * has never heard of converts as correctly as the ones it draws tabs for. A
 * table of pairs would be the thing that is wrong on the day a class is added,
 * which is the day this was written.
 */
export function classWord(sealed: string): string {
  return sealed.replace(/(?<=.)[A-Z]/g, '-$&').toLowerCase();
}

/** One node of a tenancy's registry, as its cluster prints it. */
export interface ClusterNode {
  /** The id a revocation takes. */
  id: string;
  /** `connector`, `proxy`, `admin`, `workload` or `unknown`. */
  role: string;
  common_name: string;
  fingerprint: string;
  /** Unix seconds. */
  enrolled_at: number;
  /** Present only on a node whose renewal was closed. */
  revoked_at?: number;
  /**
   * What the node called itself when it joined.
   *
   * **Asserted, never checked** — a token is minted for a role and not for a
   * machine, so there is nothing in a name for a cluster to verify. Any screen
   * showing it has to say so beside it.
   */
  claimed_name?: string;
}

/** A minted join token. The answer is the only copy. */
export interface JoinToken {
  token: string;
  ttl_secs: number;
  /** Always `connector` on this tree. */
  role: string;
}

/** What a person installs with an install key. */
export type InstallRole = 'connector' | 'client';

/**
 * A key that carries an install from this console to a terminal.
 *
 * The answer is the only copy, like a join token — but unlike one it is not
 * pasted anywhere by hand: `command` is the whole line a person copies, and the
 * key inside it is what the server recognises when the script comes back.
 */
export interface InstallKey {
  /** The one-liner, ready to copy. */
  command: string;
  /** The key on its own, for a screen that shows the two apart. */
  key: string;
  role: InstallRole;
  /** When it stops working, RFC 3339. */
  expires_at: string;
}
